(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.CommerceMemoryEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const DAY_MS = 86400000;
  const RETENTION_DAYS = 60;
  const MAX_SEARCHES = 12;
  const MAX_VIEWS = 20;
  const clean = (value, max = 160) => String(value || '').trim().replace(/\s+/g, ' ').slice(0, max);
  const now = () => Date.now();
  const cutoff = (days = RETENTION_DAYS) => now() - days * DAY_MS;
  const normalize = (value) => clean(value, 80).toLocaleLowerCase('es-MX');
  const storeKey = (value) => clean(value, 100).toLocaleLowerCase('en-US').replace(/[^a-z0-9.-]+/g, '-');
  const namespace = (key) => `commerce:${storeKey(key)}:customer-memory:v1`;
  const emptyState = () => ({ version: 1, anonymous_id: globalThis.crypto?.randomUUID?.() || `anon-${now()}-${Math.random().toString(36).slice(2)}`, searches: [], views: [], intent: 'retail', intent_at: null });
  const prune = (state) => {
    const output = { ...emptyState(), ...(state || {}) };
    output.searches = (output.searches || []).filter((item) => item.term && item.at >= cutoff()).slice(0, MAX_SEARCHES);
    output.views = (output.views || []).filter((item) => item.product_id && item.handle && item.at >= cutoff()).slice(0, MAX_VIEWS);
    if (!output.intent_at || output.intent_at < cutoff()) { output.intent = 'retail'; output.intent_at = null; }
    return output;
  };
  const read = (storage, key) => {
    try { return prune(JSON.parse(storage.getItem(namespace(key)) || 'null') || emptyState()); }
    catch { return emptyState(); }
  };
  const write = (storage, key, state) => {
    const next = prune(state);
    try { storage.setItem(namespace(key), JSON.stringify(next)); } catch {}
    return next;
  };
  const rememberSearch = (storage, key, term, resultCount = null) => {
    const value = clean(term, 80);
    if (!value) return read(storage, key);
    const state = read(storage, key);
    const normalized = normalize(value);
    state.searches = [{ term: value, normalized, at: now(), result_count: Number.isInteger(resultCount) ? resultCount : null }, ...state.searches.filter((item) => (item.normalized || normalize(item.term)) !== normalized)].slice(0, MAX_SEARCHES);
    return write(storage, key, state);
  };
  const deleteSearch = (storage, key, normalized) => {
    const state = read(storage, key);
    state.searches = state.searches.filter((item) => (item.normalized || normalize(item.term)) !== normalized);
    return write(storage, key, state);
  };
  const clearSearches = (storage, key) => {
    const state = read(storage, key);
    state.searches = [];
    return write(storage, key, state);
  };
  const rememberView = (storage, key, product, dwellMs = 0) => {
    if (!product?.product_id || !product?.handle) return read(storage, key);
    const state = read(storage, key);
    const old = state.views.find((item) => String(item.product_id) === String(product.product_id));
    const dwell_bucket = dwellMs < 3000 ? 'LT_3S' : dwellMs < 10000 ? '3_10S' : dwellMs < 30000 ? '10_30S' : 'GT_30S';
    state.views = [{ product_id: product.product_id, variant_id: product.variant_id || null, handle: clean(product.handle, 160), title: clean(product.title, 160), url: clean(product.url, 260), image: clean(product.image, 500), price_cents: Number.isFinite(Number(product.price_cents)) ? Number(product.price_cents) : null, category: clean(product.category, 100), at: now(), view_count: (old?.view_count || 0) + 1, dwell_bucket, purchased: old?.purchased || false }, ...state.views.filter((item) => String(item.product_id) !== String(product.product_id))].slice(0, MAX_VIEWS);
    return write(storage, key, state);
  };
  const setIntent = (storage, key, mode) => {
    const state = read(storage, key);
    state.intent = mode === 'reventa' ? 'reventa' : 'retail';
    state.intent_at = now();
    return write(storage, key, state);
  };
  const scoreRecommendations = ({ candidates = [], memory, relations = [], cartHandles = [], limit = 4 }) => {
    const state = prune(memory || emptyState());
    const cart = new Set(cartHandles);
    const viewed = new Map(state.views.map((item) => [item.handle, item]));
    const searchTerms = state.searches.flatMap((item) => item.normalized.split(/\s+/)).filter((item) => item.length > 2);
    const relationTargets = new Map(relations.filter((item) => item.verified === true && (cart.has(item.source_handle) || viewed.has(item.source_handle))).map((item) => [item.target_handle, item.relation || 'COMPLEMENTARY']));
    return candidates.filter((item) => item?.available && item.handle && !cart.has(item.handle)).map((item) => {
      const text = `${item.title || ''} ${item.handle || ''} ${item.category || ''}`.toLocaleLowerCase('es-MX');
      let score = 0;
      let recommendation_reason = 'exploration';
      if (relationTargets.has(item.handle)) { score += 50; recommendation_reason = `cross_sell:${relationTargets.get(item.handle).toLocaleLowerCase('es-MX')}`; }
      if (searchTerms.some((term) => text.includes(term))) { score += 35; if (recommendation_reason === 'exploration') recommendation_reason = 'recent_search'; }
      const prior = viewed.get(item.handle);
      if (prior) { score += 20 + Math.min(prior.view_count || 1, 3) * 5; if (recommendation_reason === 'exploration') recommendation_reason = 'recently_viewed'; }
      if (state.intent === 'reventa' && item.wholesale_eligible === true) { score += 20; if (recommendation_reason === 'exploration') recommendation_reason = 'reventa'; }
      if (!score) score = 1;
      return { ...item, score, recommendation_reason };
    }).sort((a, b) => b.score - a.score || String(a.handle).localeCompare(String(b.handle))).slice(0, limit);
  };
  const zeroResultRoute = ({ similar = [], localOrder = [], chinaOrder = [], localEnabled = false, chinaEnabled = false }) => {
    const own = similar.find((item) => item.available === true);
    if (own) return { type: 'SIMILAR_PRODUCT', item: own, quick_add: false };
    if (localEnabled && localOrder.length) return { type: 'LOCAL_ORDER', item: localOrder[0], availability_label: 'Disponibilidad por confirmar', quick_add: false };
    if (chinaEnabled && chinaOrder.length) return { type: 'CHINA_SPECIAL_ORDER', item: chinaOrder[0], availability_label: 'Disponibilidad por confirmar', quick_add: false };
    return { type: 'AI_OR_CONTACT', quick_add: false };
  };
  const cartReturnState = (cart) => cart?.item_count > 0 ? { visible: true, title: 'Tu carrito sigue aquí', item_count: cart.item_count } : { visible: false, item_count: 0 };

  return { RETENTION_DAYS, namespace, emptyState, prune, read, write, rememberSearch, deleteSearch, clearSearches, rememberView, setIntent, scoreRecommendations, zeroResultRoute, cartReturnState };
});
